import { Component, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { InsuranceTableComponent } from '../../../componentes/insurance-table/insurance-table.component';
import { ExportarComponent } from '../../../componentes/exportar/exportar.component';
import { CamposComponent } from '../../../componentes/campos/campos.component';
import { TableConfig } from '../../../interfaces/table-config.interface';
import { ProduccionFilter } from '../../../interfaces/filters.interfaces';
import {
  ProductionRequest,
  ProductionResponse,
  ProductionData,
} from '../../../interfaces/micartera.interfaces';
import { ApiResponse } from '../../../interfaces/common.interfaces';
import { MicarteraService } from '../../../services/micartera.service';
import { AuthService } from '../../../services/auth.service';
import { HttpResponse } from '@angular/common/http';
import { GoogleAnalyticsService } from '../../../services/google-analytics/google-analytics.service';

/**
 * Componente para gestionar la producción de pólizas
 * Permite visualizar y exportar información sobre la producción de pólizas
 */
@Component({
  selector: 'app-produccion',
  standalone: true,
  imports: [
    InsuranceTableComponent,
    ExportarComponent,
    CamposComponent,
    CommonModule,
  ],
  templateUrl: './produccion.component.html',
  styleUrls: ['./produccion.component.css'],
})
export class ProduccionComponent {
  produccionData: ProductionResponse[] = [];
  mensajeNoData: string = 'Selecciona los campos que deseas buscar';
  errorStatus?: number;
  title: string = '';
  fecha1?: Date;
  fecha2?: Date;
  pageNumber: number = 1;
  pageSize: number = 6;
  totalRecordsFromServer: number = 0;
  totalPagesFromServer: number = 0;
  /**
   * Totales del servidor para la tabla. Usa `primaTotal` como total de la columna Prima.
   */
  tableTotals?: { [key: string]: number };
  private lastFilters?: ProduccionFilter;
  tableConfig: TableConfig = {
    columns: [
      { key: 'policyNumber', label: 'Póliza', sortable: true, type: 'poliza' },
      { key: 'clientCode', label: 'Código', sortable: true },
      { key: 'clientName', label: 'Asegurado', sortable: true },
      { key: 'abrType', label: 'Tipo', sortable: true },
      { key: 'documentNumber', label: 'Número', sortable: true },
      {
        key: 'efectiveDate',
        label: 'Efectividad',
        sortable: true,
        type: 'date', // Cambiado de 'text' a 'date'
      },
      {
        key: 'workDate',
        label: 'Fecha emitido',
        sortable: true,
        type: 'date', // Cambiado de 'text' a 'date'
      },
      {
        key: 'premium',
        label: 'Prima',
        sortable: true,
        type: 'currency', // Ya está correcto como currency
      },
      {
        key: 'tax',
        label: 'Impuesto',
        sortable: true,
        type: 'currency', // Agregado tipo currency para impuestos
      },
    ],
    showTotals: true,
    totalFields: ['premium'],
  };

  opcionesSelectFilter = [
    { id: 'poliza', nombre: 'Póliza' },
    { id: 'codigo', nombre: 'Código' },
    { id: 'cedula', nombre: 'Cédula' },
    { id: 'rnc', nombre: 'RNC' },
    { id: 'nombre', nombre: 'Nombre' },
  ];

  @ViewChild(InsuranceTableComponent) tablaComponent!: InsuranceTableComponent;

  /**
   * Constructor del componente
   * @param micarteraService Servicio para obtener datos de la cartera
   * @param authService Servicio de autenticación
   */
  constructor(
    private micarteraService: MicarteraService,
    private authService: AuthService,
    private gAnalytics:GoogleAnalyticsService
  ) {
    this.title = 'Producción';
  }

  /**
   * Maneja los cambios en los filtros de búsqueda
   * Realiza una nueva consulta al servidor con los filtros actualizados
   * @param filters Objeto con los filtros seleccionados por el usuario
   */
  onFilterChange(filters: ProduccionFilter): void {
    if (Object.keys(filters).length === 0) {
      this.produccionData = [];
      this.tableTotals = undefined;
      this.mensajeNoData = 'Selecciona los campos que deseas buscar';
      this.errorStatus = undefined;
      return;
    }

    this.fecha1 = filters.dateFrom;
    this.fecha2 = filters.dateTo!;
    this.lastFilters = filters;
    this.pageNumber = 1;

    const request: ProductionRequest = {
      producerNumber: this.authService.getProducerNumber() || 0,
      dateFrom: filters.dateFrom || new Date(),
      dateTo: filters.dateTo || new Date(),
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
      ...(filters?.policyNumber && { policy: filters.policyNumber }),
      ...(filters?.clientCode && { ClientCode: filters.clientCode }),
    };

    // Agregar campos opcionales solo si tienen valor
    if (filters.lobId) request.lob = filters.lobId;
    if (filters.sLobId) request.slob = filters.sLobId;
    if (filters.clientCode) request.clientCode = filters.clientCode;
    if (filters.documentType) request.documentType = filters.documentType;
    if (filters.currency) request.currency = filters.currency;

    this.micarteraService.getProduction(request).subscribe({
      next: (response: HttpResponse<ApiResponse<ProductionData>>) => {
        if (response.status === 200) {
          this.produccionData = response.body?.data?.productions || [];
          this.totalRecordsFromServer =
            response.body?.data?.pagination?.totalRecords || 0;
          this.totalPagesFromServer =
            response.body?.data?.pagination?.totalPages || 0;
          this.tableTotals = {
            premium: response.body?.data?.pagination?.primaTotal ?? 0,
          };
          this.errorStatus = undefined;
        } else if (response.status === 204) {
          this.produccionData = [];
          this.tableTotals = undefined;
          this.mensajeNoData = 'No hay datos disponibles para mostrar';
        }
      },
      error: (error) => {
        console.error('Error al obtener la producción:', error);
        this.produccionData = [];
        this.tableTotals = undefined;
        this.mensajeNoData =
          'No se han podido obtener los datos de la producción';
      },
    });
  }

  /**
   * Solicita al API la página seleccionada en la tabla
   * @param page Número de página a consultar
   */
  onPageChange(page: number): void {
    this.pageNumber = page;
    const filters = this.lastFilters;

    const request: ProductionRequest = {
      producerNumber: this.authService.getProducerNumber() || 0,
      dateFrom: filters?.dateFrom || this.fecha1 || new Date(),
      dateTo: filters?.dateTo || this.fecha2 || new Date(),
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
      ...(filters?.policyNumber && { policy: filters.policyNumber }),
      ...(filters?.clientCode && { ClientCode: filters.clientCode }),
    };

    if (filters?.lobId) request.lob = filters.lobId;
    if (filters?.sLobId) request.slob = filters.sLobId;
    if (filters?.clientCode) request.clientCode = filters.clientCode;
    if (filters?.documentType) request.documentType = filters.documentType;
    if (filters?.currency) request.currency = filters.currency;

    this.micarteraService.getProduction(request).subscribe({
      next: (response: HttpResponse<ApiResponse<ProductionData>>) => {
        if (response.status === 200) {
          this.produccionData = response.body?.data?.productions || [];
          this.totalRecordsFromServer =
            response.body?.data?.pagination?.totalRecords || 0;
          this.totalPagesFromServer =
            response.body?.data?.pagination?.totalPages || 0;
          this.tableTotals = {
            premium: response.body?.data?.pagination?.primaTotal ?? 0,
          };
          this.errorStatus = undefined;
        } else if (response.status === 204) {
          this.produccionData = [];
          this.tableTotals = undefined;
          this.mensajeNoData = 'No hay datos disponibles para mostrar';
        }
      },
      error: (error) => {
        console.error('Error al obtener la producción:', error);
        this.produccionData = [];
        this.tableTotals = undefined;
        this.mensajeNoData =
          'No se han podido obtener los datos de la producción';
      },
    });
  }

  /**
   * Exporta todos los registros del reporte según los filtros actuales
   * Consulta el total de producción al API y los usa solo para la exportación
   * @param formato Formato de exportación deseado
   */
  onExportar(formato: string): void {
    if (!this.tablaComponent || this.totalRecordsFromServer === 0) {
      return;
    }

    const headerInfo = {
      titulo: 'Mi Producción',
      lob: this.getLobName(),
      slob: this.getSlobName(),
      fecha1: this.fecha1!,
      fecha2: this.fecha2,
      tableConfig: this.tableConfig,
    };

    this.gAnalytics.trackEvent('produccion_exportar', {
      page: '/produccion',
      format: formato,
    });

    const filters = this.lastFilters;
    const request: ProductionRequest = {
      producerNumber: this.authService.getProducerNumber() || 0,
      dateFrom: filters?.dateFrom || this.fecha1 || new Date(),
      dateTo: filters?.dateTo || this.fecha2 || new Date(),
      pageNumber: 1,
      pageSize: this.totalRecordsFromServer,
      ...(filters?.policyNumber && { policy: filters.policyNumber }),
      ...(filters?.clientCode && { ClientCode: filters.clientCode }),
    };

    if (filters?.lobId) request.lob = filters.lobId;
    if (filters?.sLobId) request.slob = filters.sLobId;
    if (filters?.clientCode) request.clientCode = filters.clientCode;
    if (filters?.documentType) request.documentType = filters.documentType;
    if (filters?.currency) request.currency = filters.currency;

    this.micarteraService.getProduction(request).subscribe({
      next: (response: HttpResponse<ApiResponse<ProductionData>>) => {
        if (response.status !== 200) {
          return;
        }

        const allData = response.body?.data?.productions || [];
        const dataOriginal = this.tablaComponent.data;
        this.tablaComponent.data = allData;
        this.tablaComponent.exportarDatos(formato, headerInfo);
        this.tablaComponent.data = dataOriginal;
      },
      error: (error) => {
        console.error('Error al obtener la producción para exportar:', error);
      },
    });
  }

  @ViewChild(CamposComponent) camposComponent!: CamposComponent;

  /**
   * Obtiene el nombre del ramo (LOB) seleccionado
   * @returns Nombre del ramo o cadena vacía si no hay selección
   */
  public getLobName(): string {
    const selectedLob = this.camposComponent?.lobs.find(
      (lob) => lob.lobId === Number(this.camposComponent?.selectedLob)
    );
    return selectedLob?.lobName || '';
  }

  /**
   * Obtiene el nombre del subramo (SLOB) seleccionado
   * @returns Nombre del subramo o cadena vacía si no hay selección
   */
  public getSlobName(): string {
    const selectedSLob = this.camposComponent?.filteredSLobs.find(
      (slob) => slob.sLobId === Number(this.camposComponent?.selectedSLob)
    );

    return selectedSLob?.sLobName || '';
  }


 /**
 * Obtiene el nombre del tipo de documento seleccionado
 * @returns Nombre del tipo de documento o cadena vacía si no hay selección
 */
public getDocumentTypeName(): string {
  if (!this.camposComponent?.tipoDoc || !this.camposComponent?.documentTypes) {
    return '';
  }

  const tipoDocId = Number(this.camposComponent.tipoDoc);
  
  if (isNaN(tipoDocId)) {
    return '';
  }

  const selectedDocumentType = this.camposComponent.documentTypes.find(
    (docType) => docType.documentTypeId === tipoDocId
  );

  console.log(selectedDocumentType?.documentTypeName)
  
  return selectedDocumentType?.documentTypeName || '';
}
}
